import { useState, useCallback, memo } from 'react'

interface JsonTreeProps {
  data: unknown
  name?: string
  defaultExpanded?: boolean
  searchTerm?: string
  depth?: number
}

function matchesSearch(key: string, value: unknown, term: string): boolean {
  if (!term) return true
  const lower = term.toLowerCase()
  if (key.toLowerCase().includes(lower)) return true
  if (typeof value === 'string' && value.toLowerCase().includes(lower)) return true
  if (typeof value === 'number' && String(value).includes(term)) return true
  if (typeof value === 'boolean' && String(value) === term) return true
  return false
}

function getTypeLabel(value: unknown): string {
  if (value === null) return 'null'
  if (value === undefined) return 'undefined'
  if (Array.isArray(value)) return `Array(${value.length})`
  if (typeof value === 'object') return `Object(${Object.keys(value as object).length})`
  return typeof value
}

function ValueDisplay({ value }: { value: unknown }) {
  if (value === null) return <span className="text-orange-400">null</span>
  if (value === undefined) return <span className="text-muted-foreground">undefined</span>
  if (typeof value === 'string') return <span className="text-primary">"{value.length > 120 ? value.slice(0, 120) + '...' : value}"</span>
  if (typeof value === 'number') return <span className="text-blue-400">{value}</span>
  if (typeof value === 'boolean') return <span className="text-purple-400">{String(value)}</span>
  return null
}

const JsonTreeNode = memo(function JsonTreeNode({ data, name, defaultExpanded = false, searchTerm = '', depth = 0 }: JsonTreeProps) {
  const [expanded, setExpanded] = useState(defaultExpanded || depth < 1)

  const toggle = useCallback(() => setExpanded((e) => !e), [])

  const isExpandable = data !== null && typeof data === 'object'

  if (!isExpandable) {
    return (
      <div className="flex items-baseline gap-1 py-px" style={{ paddingLeft: depth * 12 }}>
        {name != null && <span className="text-foreground/70 shrink-0">{name}:</span>}
        <ValueDisplay value={data} />
      </div>
    )
  }

  const entries = Array.isArray(data)
    ? data.map((v, i) => [String(i), v] as const)
    : Object.entries(data as Record<string, unknown>)

  const filtered = searchTerm
    ? entries.filter(([k, v]) => {
        if (matchesSearch(k, v, searchTerm)) return true
        // Check children for nested matches
        if (v !== null && typeof v === 'object') return true
        return false
      })
    : entries

  return (
    <div>
      <div
        className="flex items-baseline gap-1 py-px cursor-pointer hover:bg-muted/50 rounded-sm"
        style={{ paddingLeft: depth * 12 }}
        onClick={toggle}
      >
        <span className="text-muted-foreground text-xs w-3 shrink-0">{expanded ? '\u25BC' : '\u25B6'}</span>
        {name != null && <span className="text-foreground/70 shrink-0">{name}:</span>}
        <span className="text-muted-foreground text-xs">{getTypeLabel(data)}</span>
      </div>
      {expanded && (
        <div>
          {filtered.map(([key, value]) => (
            <JsonTreeNode
              key={key}
              name={key}
              data={value}
              searchTerm={searchTerm}
              depth={depth + 1}
            />
          ))}
          {filtered.length === 0 && (
            <div className="text-muted-foreground text-xs py-px" style={{ paddingLeft: (depth + 1) * 12 }}>
              (empty)
            </div>
          )}
        </div>
      )}
    </div>
  )
})

export function JsonTree({ data, name, defaultExpanded, searchTerm }: JsonTreeProps) {
  return (
    <div className="font-mono text-xs leading-relaxed">
      <JsonTreeNode data={data} name={name} defaultExpanded={defaultExpanded} searchTerm={searchTerm} />
    </div>
  )
}
